import { FormEvent, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateFacility } from '../../lib/identityRegistry';
import { describeFacilityError } from './facilityHelpers';
import { AreaCreateDialog } from './AreaCreateDialog';
import { ConfirmDialog } from '../ConfirmDialog';
import type { AreaRecord, FacilityRecord } from '../../types/registry';

interface FacilityEditDrawerProps {
  facility: FacilityRecord;
  onClose: () => void;
}

export function FacilityEditDrawer({ facility, onClose }: FacilityEditDrawerProps): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState(facility.name ?? '');
  const [address, setAddress] = useState(facility.address ?? '');
  const [city, setCity] = useState(facility.city ?? '');
  const [country, setCountry] = useState(facility.country ?? '');
  const [areas, setAreas] = useState<AreaRecord[]>(() =>
    (facility.area_ids ?? []).map((area) => ({ ...area })),
  );
  const [addingArea, setAddingArea] = useState(false);
  const [pendingArea, setPendingArea] = useState<AreaRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['facilities'] });

  const fieldsMutation = useMutation({
    mutationFn: () =>
      updateFacility(facility.facility_id, {
        name: name.trim(),
        address,
        city,
        country,
      }),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
    onError: (mutationError) => setError(describeFacilityError(mutationError)),
  });

  // Adding and removing areas share one persistence path: PUT the full area list.
  const areasMutation = useMutation({
    mutationFn: (nextAreas: AreaRecord[]) =>
      updateFacility(facility.facility_id, {
        area_ids: nextAreas.map((area) => ({
          area_id: area.area_id,
          name: area.name.trim(),
          floor: area.floor ?? 0,
          description: area.description ?? '',
        })),
      }),
    onSuccess: async (updated) => {
      setAreas((updated.area_ids ?? []).map((area) => ({ ...area })));
      setAddingArea(false);
      setPendingArea(null);
      setError(null);
      await invalidate();
    },
    onError: (mutationError) => {
      setError(describeFacilityError(mutationError));
      setPendingArea(null);
    },
  });

  function handleSaveFields(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    fieldsMutation.mutate();
  }

  function handleCreateArea(area: AreaRecord) {
    setError(null);
    areasMutation.mutate([...areas, area]);
  }

  function handleConfirmRemove() {
    if (!pendingArea) return;
    setError(null);
    areasMutation.mutate(areas.filter((area) => area.area_id !== pendingArea.area_id));
  }

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose}>
        <aside className="drawer" onClick={(event) => event.stopPropagation()}>
        <h2>Edit facility</h2>
        <p className="panel__copy">{facility.facility_id}</p>

        <form className="invite-form" onSubmit={handleSaveFields}>
          <label className="form-field">
            <span>Name</span>
            <input value={name} onChange={(event) => setName(event.target.value)} required />
          </label>
          <label className="form-field">
            <span>Address</span>
            <input value={address} onChange={(event) => setAddress(event.target.value)} />
          </label>
          <label className="form-field">
            <span>City</span>
            <input value={city} onChange={(event) => setCity(event.target.value)} />
          </label>
          <label className="form-field">
            <span>Country</span>
            <input value={country} onChange={(event) => setCountry(event.target.value)} />
          </label>

          {error ? <div className="form-error">{error}</div> : null}

          <div className="drawer__actions">
            <button className="primary-button" disabled={fieldsMutation.isPending} type="submit">
              {fieldsMutation.isPending ? 'Saving...' : 'Save facility'}
            </button>
            <button className="ghost-button" type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>

        <div className="facility-picker">
          <span>Areas</span>
          {!areas.length ? <p className="hint-text">No areas yet.</p> : null}
          {areas.map((area) => (
            <div key={area.area_id} className="facility-block">
              <p className="facility-block__title">{area.name}</p>
              <p className="hint-text">
                {[
                  typeof area.floor === 'number' ? `Floor ${area.floor}` : null,
                  area.description || null,
                ]
                  .filter(Boolean)
                  .join(' · ') || 'No details'}
              </p>
              <button
                className="ghost-button"
                type="button"
                disabled={areasMutation.isPending}
                onClick={() => setPendingArea(area)}
              >
                Remove area
              </button>
            </div>
          ))}
          <button
            className="secondary-button"
            type="button"
            disabled={areasMutation.isPending}
            onClick={() => {
              setError(null);
              setAddingArea(true);
            }}
          >
            Add area
          </button>
        </div>
        </aside>
      </div>

      {addingArea ? (
        <AreaCreateDialog
          saving={areasMutation.isPending}
          error={error}
          onCreate={handleCreateArea}
          onCancel={() => {
            setAddingArea(false);
            setError(null);
          }}
        />
      ) : null}

      {pendingArea ? (
        <ConfirmDialog
          title="Remove area"
          message={`Remove area "${pendingArea.name}" from this facility?`}
          confirmLabel="Remove area"
          busy={areasMutation.isPending}
          onConfirm={handleConfirmRemove}
          onCancel={() => setPendingArea(null)}
        />
      ) : null}
    </>
  );
}
